import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { FuseConfigService } from '@fuse/services/config';
import { Subject, takeUntil } from 'rxjs';
import { themes } from 'app/config/theme.config';

interface ThemeOption {
    id: string;
    name: string;
    color: string;
}

@Component({
    selector: 'themes',
    templateUrl: './themes.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'themes',
    standalone: true,
    imports: [
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        NgClass
    ],
})
export class ThemesComponent implements OnInit, OnDestroy {
    availableThemes: ThemeOption[];
    currentTheme: string;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseConfigService: FuseConfigService
    ) {
        // Initialize with default theme
        this.availableThemes = [];
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to config changes
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {
                // Store the current theme
                this.currentTheme = config.theme;

                // Get available themes from config
                if (config.themes) {
                    this.availableThemes = config.themes.map(theme => ({
                        id: theme.id,
                        name: theme.name,
                        // Get color from theme configuration
                        color: this.getThemeColor(theme.id)
                    }));
                }

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get theme color based on theme ID
     */
    private getThemeColor(themeId: string): string {
        // Get the theme name without the 'theme-' prefix
        const themeName = themeId.replace('theme-', '');
        
        // Get the theme configuration
        const themeConfig = themes[themeName] || themes.default;
        
        // Return the primary color
        return themeConfig.primary.DEFAULT;
    }

    /**
     * Get current theme color
     */
    getCurrentThemeColor(): string {
        const currentThemeOption = this.availableThemes.find(t => t.id === this.currentTheme);
        return currentThemeOption ? currentThemeOption.color : this.getThemeColor('theme-default');
    }

    /**
     * Set the active theme
     *
     * @param theme
     */
    setActiveTheme(theme: string): void {
        // Set the active theme
        this._fuseConfigService.config = { theme };
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }
} 